import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, useDarkMode } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import { CheckmarkAshIcon } from '@glu/icons-react';
import LabelText from '../LabelText/LabelText';
import styles from './CheckboxRadio.styles';
import Error from '../Error/Error';

const useStyles = createUseStyles(styles);

let uniqueId = 0;

const CheckboxRadio = (props) => {
  const {
    checked,
    className,
    dark: propDark,
    error,
    errors,
    htmlId,
    htmlValue,
    indeterminate,
    inline,
    inputClass,
    labelClass,
    labelTagProps,
    labelText,
    name,
    onChange,
    onKeyDown,
    screenReaderLabel,
    theme,
    type,
    value: fieldValue,
    ...rest
  } = props;
  const darkMode = useDarkMode();
  const dark = propDark || darkMode;
  const classes = useStyles({ ...props, dark });

  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `checkbox-radio-${uniqueId++}`, [htmlId]);
  const [isSelected, setIsSelected] = useState(
    checked === undefined ? htmlValue === fieldValue : checked
  );

  const errorMessage = error || errors?.[0];

  useEffect(() => {
    setIsSelected(checked === undefined ? htmlValue === fieldValue : checked);
  }, [checked, fieldValue, htmlValue, isSelected]);

  const handleChange = ({ target: { checked: newChecked } }) => {
    setIsSelected(newChecked);
    onChange(name, newChecked ? htmlValue : newChecked);
  };

  return (
    <label className={`${classes.root} ${className}`} htmlFor={id} {...labelTagProps}>
      <input
        id={id}
        ref={el => el && (el.indeterminate = indeterminate)} // eslint-disable-line
        type={type}
        checked={isSelected}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        className={inputClass}
        value={htmlValue}
        name={name}
        {...rest}
      />
      <span
        className={`
          ${classes.icon} 
          ${type === 'checkbox' ? classes.checkboxIcon : classes.radioIcon}
          ${isSelected ? classes.selected : ''}
        `}
      >
        {type === 'checkbox'
          ? indeterminate
            ? <span className={classes.dash}>–</span>
            : isSelected && <CheckmarkAshIcon />
          : <span className={classes.radioDot} />}
      </span>
      <LabelText
        label={labelText}
        className={labelClass}
        screenReaderLabel={screenReaderLabel}
        hasError={!!errorMessage}
        checkboxLabel
      />
      <Error error={errorMessage} />
    </label>
  );
};

const WithInputCheckboxRadio = withInputAPI(CheckboxRadio);
WithInputCheckboxRadio.displayName = 'CheckboxRadio';
export const BareCheckboxRadio = CheckboxRadio;
export default WithInputCheckboxRadio;
export const ValidatedCheckboxRadio = WithInputCheckboxRadio;

CheckboxRadio.propTypes = {

  /** Determines whether checkbox is checked */
  checked: PropTypes.bool,

  /** Classname to apply to container label element */
  className: PropTypes.string,

  /** dark mode boolean */
  dark: PropTypes.bool,

  /** The error to display */
  error: PropTypes.string,

  /** Full errors object. */
  errors: PropTypes.arrayOf(PropTypes.string),

  /** form field id */
  htmlId: PropTypes.string,

  /** element value */
  htmlValue: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.string, PropTypes.number
  ]),

  /** Render checkbox in an indeterminate state */
  indeterminate: PropTypes.bool,

  /** Determines if inputs should be oriented inline or block */
  inline: PropTypes.bool,

  /** Classname to apply to checkbox input */
  inputClass: PropTypes.string,

  /** Classname to apply to checkbox label span */
  labelClass: PropTypes.string,

  /** Properties to pass to the label tag */
  labelTagProps: PropTypes.shape({}),

  /** text label for checkbox */
  labelText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]).isRequired,

  /** form field name */
  name: PropTypes.string.isRequired,

  /** function to call when checkbox changes */
  onChange: PropTypes.func.isRequired,

  /** function to call when checkbox changes */
  onKeyDown: PropTypes.func,

  /** Determines if label should be screen reader only */
  screenReaderLabel: PropTypes.bool,

  /** Theme used for styling */
  theme: PropTypes.objectOf(PropTypes.any),

  /** Determines if type is "checkbox" or "radio" */
  type: PropTypes.oneOf(['checkbox', 'radio']),

  /** value of the named element provided by form context */
  value: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.string, PropTypes.number
  ])
};
CheckboxRadio.defaultProps = {
  checked: undefined,
  className: '',
  dark: false,
  error: '',
  errors: undefined,
  htmlId: undefined,
  htmlValue: true,
  indeterminate: false,
  inline: false,
  inputClass: '',
  labelClass: '',
  labelTagProps: {},
  onKeyDown: undefined,
  screenReaderLabel: false,
  theme: undefined,
  type: 'checkbox',
  value: false
};
